import React from 'react';

// Heading component for typography
const HeadingComponent = ({ 
  text = 'Überschrift',
  level = 'h2',
  align = 'left',
  color = 'slate-900',
  weight = 'bold',
  fontSize = 'default',
  backgroundColor = 'transparent',
  padding = 'none'
}) => {
  const Tag = level;
  
  const getSizeClass = () => {
    // If custom fontSize is specified, use it instead of level-based sizing
    if (fontSize !== 'default') {
      switch (fontSize) {
        case 'xs': return 'text-xs';
        case 'sm': return 'text-sm';
        case 'base': return 'text-base';
        case 'lg': return 'text-lg';
        case 'xl': return 'text-xl';
        case '2xl': return 'text-2xl';
        case '3xl': return 'text-3xl';
        case '4xl': return 'text-4xl';
        case '5xl': return 'text-5xl';
        case '6xl': return 'text-6xl';
        case '7xl': return 'text-7xl';
        case '8xl': return 'text-8xl';
        case '9xl': return 'text-9xl';
        default: return 'text-3xl';
      }
    }
    
    // Default level-based sizing
    switch (level) {
      case 'h1': return 'text-4xl';
      case 'h2': return 'text-3xl';
      case 'h3': return 'text-2xl';
      case 'h4': return 'text-xl';
      case 'h5': return 'text-lg';
      case 'h6': return 'text-base';
      default: return 'text-3xl';
    }
  };

  const getWeightClass = () => {
    switch (weight) {
      case 'thin': return 'font-thin';
      case 'light': return 'font-light';
      case 'normal': return 'font-normal';
      case 'medium': return 'font-medium';
      case 'semibold': return 'font-semibold';
      case 'bold': return 'font-bold';
      case 'extrabold': return 'font-extrabold';
      case 'black': return 'font-black';
      default: return 'font-bold';
    }
  };

  const getAlignClass = () => {
    switch (align) {
      case 'left': return 'text-left';
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      case 'justify': return 'text-justify';
      default: return 'text-left';
    }
  };

  const getColorClass = () => {
    switch (color) {
      case 'slate-900': return 'text-slate-900 dark:text-slate-100';
      case 'slate-700': return 'text-slate-700 dark:text-slate-300';
      case 'slate-500': return 'text-slate-500 dark:text-slate-400';
      case 'blue-600': return 'text-blue-600 dark:text-blue-400';
      case 'green-600': return 'text-green-600 dark:text-green-400';
      case 'red-600': return 'text-red-600 dark:text-red-400';
      case 'yellow-600': return 'text-yellow-600 dark:text-yellow-400';
      case 'purple-600': return 'text-purple-600 dark:text-purple-400';
      default: return 'text-slate-900 dark:text-slate-100';
    }
  };

  return (
    <Tag 
      className={`${getSizeClass()} ${getWeightClass()} ${getAlignClass()} ${getColorClass()} leading-tight`}
    >
      {text}
    </Tag>
  );
};

export const Heading = {
  fields: {
    text: {
      type: 'textarea',
      label: 'Text'
    },
    level: {
      type: 'select',
      label: 'Überschriftenebene',
      options: [
        { label: 'H1 (Größte)', value: 'h1' },
        { label: 'H2 (Groß)', value: 'h2' },
        { label: 'H3 (Mittel)', value: 'h3' },
        { label: 'H4 (Klein)', value: 'h4' },
        { label: 'H5 (Kleiner)', value: 'h5' },
        { label: 'H6 (Kleinste)', value: 'h6' }
      ]
    },
    align: {
      type: 'select',
      label: 'Ausrichtung',
      options: [
        { label: 'Links', value: 'left' },
        { label: 'Zentriert', value: 'center' },
        { label: 'Rechts', value: 'right' },
        { label: 'Blocksatz', value: 'justify' }
      ]
    },
    color: {
      type: 'select',
      label: 'Farbe',
      options: [
        { label: 'Dunkelgrau', value: 'slate-900' },
        { label: 'Mittelgrau', value: 'slate-700' },
        { label: 'Hellgrau', value: 'slate-500' },
        { label: 'Blau', value: 'blue-600' },
        { label: 'Grün', value: 'green-600' },
        { label: 'Rot', value: 'red-600' },
        { label: 'Gelb', value: 'yellow-600' },
        { label: 'Lila', value: 'purple-600' }
      ]
    },
    weight: {
      type: 'select',
      label: 'Schriftgewicht',
      options: [
        { label: 'Dünn', value: 'thin' },
        { label: 'Leicht', value: 'light' },
        { label: 'Normal', value: 'normal' },
        { label: 'Mittel', value: 'medium' },
        { label: 'Halbfett', value: 'semibold' },
        { label: 'Fett', value: 'bold' },
        { label: 'Extrafett', value: 'extrabold' },
        { label: 'Schwarz', value: 'black' }
      ]
    },
    fontSize: {
      type: 'select',
      label: 'Schriftgröße',
      options: [
        { label: 'Standard (nach Ebene)', value: 'default' },
        { label: 'Sehr klein (XS)', value: 'xs' },
        { label: 'Klein (SM)', value: 'sm' },
        { label: 'Normal (Base)', value: 'base' },
        { label: 'Groß (LG)', value: 'lg' },
        { label: 'Größer (XL)', value: 'xl' },
        { label: 'Sehr groß (2XL)', value: '2xl' },
        { label: 'Extra groß (3XL)', value: '3xl' },
        { label: 'Riesig (4XL)', value: '4xl' },
        { label: 'Mega (5XL)', value: '5xl' },
        { label: 'Giga (6XL)', value: '6xl' },
        { label: 'Ultra (7XL)', value: '7xl' },
        { label: 'Maxi (8XL)', value: '8xl' },
        { label: 'Extrem (9XL)', value: '9xl' }
      ]
    }
  },
  render: HeadingComponent
};
